import { useCallback, useEffect, useState } from "react";
import { Box, Menu, MenuItem, Tooltip, Typography, keyframes } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import NorthRoundedIcon from "@mui/icons-material/NorthRounded";
import SouthRoundedIcon from "@mui/icons-material/SouthRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import crmService from "../api/crmService";

const TEAM_ID = "__team__";

const STAGES = [
  { min: 0, name: "Inicial", color: "#9a3412" },
  { min: 25, name: "En progreso", color: "#1d4ed8" },
  { min: 50, name: "Intermedio", color: "#a16207" },
  { min: 75, name: "Avanzado", color: "#0f766e" },
  { min: 100, name: "Completado", color: "#15803d" },
];

function stageFor(pct) {
  return [...STAGES].reverse().find((stage) => pct >= stage.min) || STAGES[0];
}

function stageOf(done, target) {
  const safeTarget = Math.max(1, Number(target) || 1);
  const pct = Math.min(100, Math.round((Math.max(0, Number(done) || 0) / safeTarget) * 100));
  return stageFor(pct);
}

function formatRate(perDay) {
  if (perDay == null || Number.isNaN(Number(perDay))) return null;
  return Number(perDay).toLocaleString("es-MX", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function gapAmount(gap) {
  return Math.abs(Math.round(Number(gap) || 0));
}

function ritmoColor(pace) {
  if (pace?.perDay == null) return "#64748b";
  const ratio = Number(pace.ratio);
  const r = Number.isFinite(ratio) ? ratio : pace.gap >= 0 ? 1 : 0.5;
  const rising = Boolean(pace.rising);
  if (r >= 1 && rising) return "#059669";
  if (r >= 1) return "#16a34a";
  if (rising) return "#f59e0b";
  if (r >= 0.8) return "#f97316";
  return "#e11d48";
}

function firstName(name) {
  return String(name || "").trim().split(/\s+/)[0] || "Meta";
}

function PaceLine({ pace, team, aheadCount, size, unit = "Seguimientos" }) {
  const started = pace?.perDay != null;
  const rate = formatRate(pace?.perDay);
  const color = started ? ritmoColor(pace) : "#475569";
  let text = `${unit} / día`;
  if (started && team) {
    const onPace = aheadCount <= 0
      ? "nadie va al ritmo"
      : aheadCount >= size
        ? "todos van al ritmo"
        : `${aheadCount} de ${size} van al ritmo`;
    text = `${rate} / día · ${onPace}`;
  } else if (started && pace.rising) {
    text = `${rate} / día · vas subiendo`;
  } else if (started) {
    text = `${rate} / día`;
  }

  return (
    <Typography noWrap sx={{ fontSize: "0.78rem", fontWeight: 650, color, mt: 0.3, maxWidth: 240 }}>
      {text}
    </Typography>
  );
}

const DAY_LOAD = [0.146, 0.221, 0.188, 0.273, 0.172];
const DAY_LABELS = ["L", "M", "M", "J", "V"];
const WORK_DAYS = 5;
const BAR_W = 236;
const FILL_GRADIENT = "linear-gradient(90deg,#c2410c 0%,#2563eb 30%,#ca8a04 55%,#0d9488 78%,#16a34a 100%)";

const ghostAdvance = keyframes`
  0% { transform: scaleX(0.12); opacity: 0.18; }
  62% { transform: scaleX(1); opacity: 0.55; }
  100% { transform: scaleX(1); opacity: 0.16; }
`;

const ghostMarch = keyframes`
  from { background-position: 0 0; }
  to { background-position: 14px 0; }
`;

const iconAdvance = keyframes`
  0% { transform: translateX(-3px); opacity: 0.35; }
  45% { transform: translateX(2px); opacity: 1; }
  100% { transform: translateX(5px); opacity: 0.2; }
`;

const iconBoost = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  40% { transform: translateY(-4px) scale(1.15); }
`;

function weekLoadFor(daysOpen) {
  let load = 0;
  const open = Math.min(WORK_DAYS, Math.max(0, daysOpen || 0));
  for (let i = 0; i < open; i += 1) load += DAY_LOAD[i];
  return load;
}

function Progress({ current, target, clock, detail, pace }) {
  const safeTarget = Math.max(1, Number(target) || 80);
  const safeCurrent = Math.max(0, Number(current) || 0);
  const pct = Math.min(100, Math.round((safeCurrent / safeTarget) * 100));
  const stage = stageFor(pct);

  const started = clock?.started;
  const daysOpen = Math.min(WORK_DAYS, Math.max(0, clock?.daysOpen || 0));
  const lastDay = daysOpen >= WORK_DAYS;
  const remaining = Math.max(0, safeTarget - safeCurrent);

  const weekLoad = weekLoadFor(daysOpen);
  const expected = lastDay ? safeTarget : Math.round(safeTarget * weekLoad);
  const expectedPct = started ? Math.min(100, Math.round(weekLoad * 100)) : null;

  const fallbackProjected = !lastDay && started && weekLoad > 0 && safeCurrent > 0
    ? Math.round(safeCurrent / weekLoad)
    : null;
  const projected = lastDay ? null : (pace?.projected != null ? pace.projected : fallbackProjected);
  const projectedPct = projected != null ? Math.min(100, Math.round((projected / safeTarget) * 100)) : null;

  const cuts = [];
  let acc = 0;
  for (let i = 0; i < WORK_DAYS - 1; i += 1) {
    acc += DAY_LOAD[i];
    cuts.push(Math.round(acc * 100));
  }

  const showPaceGhost = projectedPct != null && projectedPct > pct + 1;
  const paceTint = ritmoColor(pace);
  const rising = Boolean(pace?.rising);
  const ghostSpan = showPaceGhost ? projectedPct - pct : 0;
  const labelShift = projectedPct == null ? "-50%" : projectedPct < 14 ? "0" : projectedPct > 86 ? "-100%" : "-50%";

  return (
    <>
      <Box sx={{ width: BAR_W, position: "relative", flexShrink: 0 }}>
        <Box
          sx={{
            position: "relative",
            height: 14,
            borderRadius: 99,
            bgcolor: "rgba(15,23,42,0.07)",
            boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.06)",
            overflow: "hidden",
          }}
        >
          {showPaceGhost && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${pct}%`,
                width: `${ghostSpan}%`,
                overflow: "hidden",
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  transformOrigin: "left center",
                  bgcolor: paceTint,
                  animation: `${ghostAdvance} 2.4s ease-in-out infinite`,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.42) 0 5px, transparent 5px 10px)",
                    backgroundSize: "14px 100%",
                    animation: `${ghostMarch} 0.85s linear infinite`,
                  },
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              width: `${pct}%`,
              borderRadius: 99,
              background: FILL_GRADIENT,
              backgroundSize: `${BAR_W}px 100%`,
              transition: "width .6s cubic-bezier(.2,.8,.2,1)",
            }}
          />
          {cuts.map((c, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${c}%`,
                width: "1px",
                bgcolor: "rgba(255,255,255,0.65)",
              }}
            />
          ))}
        </Box>

        {expectedPct != null && (
          <Tooltip
            title={
              lastDay
                ? remaining > 0
                  ? `Último día · ${remaining} por debajo de ${safeTarget}`
                  : `Último día · meta cubierta`
                : `Hoy deberías ir en ${expected} (día ${daysOpen} de ${WORK_DAYS})`
            }
            arrow
          >
            <Box
              sx={{
                position: "absolute",
                top: -3,
                bottom: -3,
                left: `${expectedPct}%`,
                width: "2px",
                bgcolor: "#0f172a",
                borderRadius: 99,
                transform: "translateX(-1px)",
                cursor: "default",
                zIndex: 2,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: -4,
                  left: "50%",
                  transform: "translateX(-50%)",
                  borderLeft: "3px solid transparent",
                  borderRight: "3px solid transparent",
                  borderTop: "4px solid #0f172a",
                },
              }}
            />
          </Tooltip>
        )}

        {showPaceGhost && (
          <Tooltip title={`A este ritmo terminas la semana en ~${projected} de ${safeTarget}`} arrow>
            <Box
              sx={{
                position: "absolute",
                top: -3,
                bottom: -3,
                left: `${projectedPct}%`,
                width: 0,
                borderLeft: `2px dashed ${paceTint}`,
                opacity: 0.85,
                transform: "translateX(-1px)",
                cursor: "default",
              }}
            />
          </Tooltip>
        )}

        <Box sx={{ position: "relative", height: 14, mt: 0.4 }}>
          {DAY_LABELS.map((d, i) => {
            const start = i === 0 ? 0 : cuts[i - 1];
            const end = i === WORK_DAYS - 1 ? 100 : cuts[i];
            const mid = (start + end) / 2;
            const passed = i < daysOpen;
            return (
              <Typography
                key={i}
                sx={{
                  position: "absolute",
                  left: `${mid}%`,
                  transform: "translateX(-50%)",
                  fontSize: "0.68rem",
                  fontWeight: 800,
                  lineHeight: 1,
                  color: passed ? "#0f172a" : "#94a3b8",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {d}
              </Typography>
            );
          })}
        </Box>

        {showPaceGhost && (
          <Box sx={{ position: "relative", height: 16, mt: 0.15 }}>
            <Box
              sx={{
                position: "absolute",
                left: `${projectedPct}%`,
                transform: `translateX(${labelShift})`,
                display: "flex",
                alignItems: "center",
                gap: 0.15,
                color: paceTint,
                whiteSpace: "nowrap",
              }}
            >
              <KeyboardDoubleArrowRightRoundedIcon
                sx={{
                  fontSize: 16,
                  color: paceTint,
                  animation: rising ? `${iconBoost} 1.1s ease-in-out infinite` : `${iconAdvance} 1.15s ease-in-out infinite`,
                }}
              />
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: paceTint, lineHeight: 1, letterSpacing: "-0.02em" }}>
                ritmo
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ lineHeight: 1.05, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: "0.92rem", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.03em", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
          {safeCurrent} de {safeTarget}
          <Box component="span" sx={{ fontWeight: 750, color: stage.color }}>
            {" "}· {stage.name}
          </Box>
        </Typography>
        {detail}
      </Box>
    </>
  );
}

export default function WeekGoal() {
  const [board, setBoard] = useState(null);
  const [anchor, setAnchor] = useState(null);
  const [pickedId, setPickedId] = useState(TEAM_ID);

  const load = useCallback(async () => {
    try {
      const res = await crmService.getGoals();
      setBoard(res.data?.data || null);
    } catch {
      setBoard(null);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    window.addEventListener("trazia-goal", load);
    return () => {
      clearInterval(id);
      window.removeEventListener("trazia-goal", load);
    };
  }, [load]);

  if (!board) return null;
  if (!board.seeTeam && board.me?.role === "marketer") return null;

  const members = board.seeTeam ? board.team?.members || [] : [];
  const teamView = members.length > 0;
  const team = board.team;
  const viewingTeam = teamView && (pickedId === TEAM_ID || !members.some((member) => String(member.id) === String(pickedId)));
  const selected = viewingTeam ? null : members.find((member) => String(member.id) === String(pickedId));

  if (!teamView && board.me?.goalOn === false) return null;

  const person = viewingTeam ? null : (selected || board.me);
  const personOff = Boolean(person && person.goalOn === false);
  const current = viewingTeam ? team?.totalDone : (teamView ? selected?.done : board.me?.done);
  const target = viewingTeam ? team?.totalTarget : (selected?.target || board.me?.target || board.weeklyIndividualTarget);
  const metCount = team?.metCount || 0;
  const size = team?.size || members.filter((member) => member.goalOn !== false).length;
  const label = viewingTeam ? "Equipo" : firstName(selected?.name || board.me?.name || "Meta");
  const pace = viewingTeam ? team?.pace : personOff ? null : person?.pace;
  const detail = personOff ? (
    <Typography noWrap sx={{ fontSize: "0.68rem", fontWeight: 650, color: "#64748b", mt: 0.3 }}>
      Sin meta esta semana
    </Typography>
  ) : (
    <PaceLine
      pace={pace}
      team={viewingTeam}
      aheadCount={team?.aheadCount || 0}
      size={size}
      unit={viewingTeam ? "Acciones" : (person?.unit || board.me?.unit || "Seguimientos")}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        ml: 0.75,
        pl: 1.15,
        borderLeft: "1px solid rgba(15,23,42,0.08)",
        pointerEvents: "auto",
        userSelect: "none",
        flexShrink: 1,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {teamView && (
        <>
          <Box
            component="button"
            type="button"
            aria-label="Elegir a quién ver"
            aria-haspopup="menu"
            aria-expanded={Boolean(anchor)}
            onClick={(event) => setAnchor(event.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.35,
              maxWidth: 118,
              border: "1px solid rgba(15,23,42,0.1)",
              bgcolor: "#fff",
              borderRadius: "999px",
              px: 1.1,
              py: 0.45,
              cursor: "pointer",
              font: "inherit",
              color: "#0f172a",
              "&:hover": { bgcolor: "#f8fafc" },
            }}
          >
            <Typography noWrap sx={{ fontSize: "0.78rem", fontWeight: 750, letterSpacing: "-0.02em" }}>
              {label}
            </Typography>
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: "#64748b", flexShrink: 0 }} />
          </Box>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { sx: { mt: 0.75, minWidth: 320, borderRadius: 2, maxHeight: 420 } } }}
          >
            <MenuItem
              selected={viewingTeam}
              onClick={() => {
                setPickedId(TEAM_ID);
                setAnchor(null);
              }}
              sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", py: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.86rem", fontWeight: 750 }}>Equipo completo</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "#64748b" }}>
                  {team?.pace?.perDay == null
                    ? `${metCount} de ${size} con la meta`
                    : (team.aheadCount || 0) <= 0
                      ? `nadie va al ritmo`
                      : (team.aheadCount || 0) >= size
                        ? `todos van al ritmo`
                        : `${team.aheadCount} de ${size} van al ritmo`}
                </Typography>
              </Box>
              <PaceMark pace={team?.pace} />
            </MenuItem>
            {members.map((member) => (
              <MenuItem
                key={member.id}
                selected={!viewingTeam && String(member.id) === String(selected?.id)}
                onClick={() => {
                  setPickedId(member.id);
                  setAnchor(null);
                }}
                sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", py: 1 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: "0.86rem", fontWeight: 700 }}>{member.name}</Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: member.goalOn === false ? "#64748b" : stageOf(member.done, member.target).color, fontWeight: 700 }}>
                    {member.goalOn === false ? "Sin meta" : stageOf(member.done, member.target).name}
                  </Typography>
                </Box>
                <PaceMark pace={member.goalOn === false ? null : member.pace} />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      {personOff ? (
        <Typography noWrap sx={{ fontSize: "0.86rem", fontWeight: 750, color: "#64748b" }}>
          Sin meta esta semana
        </Typography>
      ) : (
        <Progress
          current={current}
          target={target}
          clock={board.clock}
          detail={detail}
          pace={pace}
        />
      )}
    </Box>
  );
}

function PaceMark({ pace }) {
  if (pace?.perDay == null) return null;
  const n = Math.round(Number(pace.gap) || 0);
  const amount = gapAmount(pace.gap);
  const color = ritmoColor(pace);
  const iconSx = {
    fontSize: 16,
    stroke: color,
    strokeWidth: 1.25,
    ...(pace.rising ? { animation: `${iconBoost} 1.1s ease-in-out infinite` } : {}),
  };
  const up = Boolean(pace.rising) || n > 0;
  const down = !pace.rising && n < 0;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1.6rem 1.1rem",
        columnGap: 0.15,
        alignItems: "center",
        justifyItems: "center",
        flexShrink: 0,
        mt: 0.2,
        color,
      }}
    >
      <Typography sx={{ width: "100%", textAlign: "right", fontSize: "0.84rem", fontWeight: 800, color, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
        {amount > 0 ? amount : ""}
      </Typography>
      <Box sx={{ width: "1.1rem", display: "flex", justifyContent: "center" }}>
        {up ? <NorthRoundedIcon sx={iconSx} /> : down ? <SouthRoundedIcon sx={iconSx} /> : <RemoveRoundedIcon sx={iconSx} />}
      </Box>
    </Box>
  );
}
